/**
 * Hooks for managing collateralizable allowances
 * Required before issuing LOCs - vault must approve LOC contract to use collateral
 */

import { useReadContract, useWriteContract, useWaitForTransactionReceipt, useAccount } from 'wagmi'
import { CollateralVaultABI } from '@contracts/abis'
import { getContractAddress } from '@contracts/addresses'
import { Address } from '../types/contracts'
import { toast } from 'react-hot-toast'

/**
 * Hook to check collateralizable token allowance
 * Returns how much of a token the vault has approved for a specific contract (e.g., LetterOfCreditProxy)
 */
export function useCollateralizableAllowance(
  accountAddress?: Address,
  tokenAddress?: Address,
  collateralizableContractAddress?: Address
) {
  const { data: allowance, isLoading, refetch } = useReadContract({
    address: getContractAddress('CollateralVault'),
    abi: CollateralVaultABI,
    functionName: 'getCollateralizableTokenAllowance',
    args:
      accountAddress && tokenAddress && collateralizableContractAddress
        ? [accountAddress, collateralizableContractAddress, tokenAddress]
        : undefined,
    query: {
      enabled: !!accountAddress && !!tokenAddress && !!collateralizableContractAddress,
    },
  })

  return {
    allowance: allowance as bigint | undefined,
    hasAllowance: allowance ? (allowance as bigint) > 0n : false,
    isLoading,
    refetch,
  }
}

/**
 * Hook to check if a token needs approval for a specific amount
 */
export function useNeedsCollateralizableApproval(
  accountAddress?: Address,
  tokenAddress?: Address,
  collateralizableContractAddress?: Address,
  requiredAmount?: bigint
) {
  const { allowance, isLoading } = useCollateralizableAllowance(
    accountAddress,
    tokenAddress,
    collateralizableContractAddress
  )

  // If no required amount specified yet, just check if ANY allowance exists
  const needsApproval =
    allowance !== undefined && requiredAmount !== undefined
      ? allowance < requiredAmount
      : allowance !== undefined
        ? allowance === 0n  // Has allowance data but no required amount: just check > 0
        : true  // Still loading

  return {
    needsApproval,
    currentAllowance: allowance,
    isLoading,
  }
}

/**
 * Hook to modify (approve) collateralizable token allowance
 * Allows a contract (e.g., LetterOfCreditProxy) to use vault collateral
 */
export function useModifyCollateralizableAllowance() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()
  const { address: account } = useAccount()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  /**
   * Approve a contract to use collateral
   * @param tokenAddress - Token to approve (e.g., WETH)
   * @param collateralizableContractAddress - Contract to approve (e.g., LetterOfCreditProxy)
   * @param amount - Amount to approve (use max uint256 for unlimited)
   */
  const approve = async (
    tokenAddress: Address,
    collateralizableContractAddress: Address,
    amount: bigint
  ) => {
    if (!account) {
      toast.error('Please connect your wallet')
      console.error('🔴 [useModifyCollateralizableAllowance] No account connected')
      return
    }

    try {
      const vaultAddress = getContractAddress('CollateralVault')
      
      console.log('🟢 [useModifyCollateralizableAllowance] Approving collateral use', {
        vaultAddress,
        account,
        tokenAddress,
        collateralizableContractAddress,
        amount: amount.toString(),
      })

      await writeContract({
        address: vaultAddress,
        abi: CollateralVaultABI,
        functionName: 'modifyCollateralizableTokenAllowance',
        args: [
          collateralizableContractAddress, // _collateralizableContractAddress
          tokenAddress,                    // _tokenAddress
          amount,                          // _byAmount (positive = increase, uses msg.sender as account)
        ],
        gas: 150000n, // Gas for approval
      })

      console.log('🟢 [useModifyCollateralizableAllowance] Transaction submitted')
    } catch (err: any) {
      const errorMessage = err?.message || err?.toString() || 'Unknown error'
      toast.error(`Failed to approve collateral: ${errorMessage}`)
      console.error('🔴 [useModifyCollateralizableAllowance] Error:', {
        error: err,
        message: errorMessage,
        account,
        tokenAddress,
        collateralizableContractAddress,
      })
    }
  }

  /**
   * Approve unlimited collateral use (max int256)
   * Note: _byAmount is int256, not uint256, so we use max positive int256
   */
  const approveUnlimited = async (
    tokenAddress: Address,
    collateralizableContractAddress: Address
  ) => {
    // Max positive int256 = 2^255 - 1
    // MAX_UINT256 would overflow the signed int256 parameter!
    const MAX_INT256 = BigInt('0x7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')
    await approve(tokenAddress, collateralizableContractAddress, MAX_INT256)
  }

  return {
    approve,
    approveUnlimited,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

